import { useState, useEffect } from "react";
import {
  Form, Input, Button, Select, DatePicker, Radio, Divider,
  Typography, Alert, Card, Row, Col, Tag, message, Spin,
} from "antd";
import {
  UserOutlined, BankOutlined, CheckCircleOutlined,
} from "@ant-design/icons";
import { getProfile, updateProfile } from "../../api/portalService";
import { validate as validateRut, format as formatRut, clean as cleanRut } from "rut.js";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

const COMMUNES = [
  "Puerto Montt","Puerto Varas","Osorno","Chiloé","Castro",
  "Ancud","Quellón","Los Muermos","Calbuco","Maullín",
  "Santiago","Providencia","Las Condes","Vitacura","Ñuñoa",
  "Concepción","Temuco","Valparaíso","Viña del Mar","La Serena",
  "Antofagasta","Iquique","Arica","Rancagua","Talca","Chillán","Coyhaique","Punta Arenas",
];

const REGIONS = [
  "Arica y Parinacota","Tarapacá","Antofagasta","Atacama","Coquimbo",
  "Valparaíso","Metropolitana","O'Higgins","Maule","Ñuble",
  "Biobío","La Araucanía","Los Ríos","Los Lagos","Aysén","Magallanes",
];

const PortalProfile = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entityType, setEntityType] = useState("person");
  const [profileComplete, setProfileComplete] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getProfile()
      .then(({ data }) => {
        setEntityType(data.entityType || "person");
        setProfileComplete(data.profileComplete || false);
        form.setFieldsValue({
          ...data,
          entityType: data.entityType || "person",
          dob: data.dob ? dayjs(data.dob) : null,
        });
      })
      .catch(() => setError("No se pudo cargar el perfil."))
      .finally(() => setLoading(false));
  }, [form]);

  const onFinish = async (values) => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...values,
        dob: values.dob ? values.dob.toISOString() : undefined,
        profileComplete: true,
        rut: values.rut ? cleanRut(values.rut) : undefined,
        companyRut: values.companyRut ? cleanRut(values.companyRut) : undefined,
        legalRepRut: values.legalRepRut ? cleanRut(values.legalRepRut) : undefined,
      };
      await updateProfile(payload);
      setProfileComplete(true);
      message.success("Perfil guardado correctamente");
    } catch (e) {
      setError(e?.response?.data?.message || "Error al guardar el perfil.");
    } finally {
      setSaving(false);
    }
  };

  const onRutBlur = (field) => (e) => {
    const raw = e.target.value;
    if (raw && validateRut(raw)) {
      form.setFieldsValue({ [field]: formatRut(cleanRut(raw)) });
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
        <Title level={3} style={{ margin: 0, color: "#1d3557" }}>Mi Perfil</Title>
        {profileComplete ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Perfil completo</Tag>
        ) : (
          <Tag color="warning">Completa tu perfil para agilizar tus trámites</Tag>
        )}
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <Form form={form} layout="vertical" onFinish={onFinish} size="large">
        {/* Tipo de entidad */}
        <Card
          style={{ marginBottom: 16, borderRadius: 8 }}
          title={<Text strong>Tipo de persona</Text>}
        >
          <Form.Item name="entityType" noStyle>
            <Radio.Group
              onChange={(e) => setEntityType(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="person">
                <UserOutlined /> Persona natural
              </Radio.Button>
              <Radio.Button value="company">
                <BankOutlined /> Empresa
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Card>

        {/* Datos personales */}
        <Card
          style={{ marginBottom: 16, borderRadius: 8 }}
          title={<Text strong>{entityType === "company" ? "Datos del representante legal" : "Datos personales"}</Text>}
        >
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Primer nombre"
                name="name"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Input placeholder="Ej: Juan" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Segundo nombre" name="secondName">
                <Input placeholder="Ej: Carlos" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Apellido paterno"
                name="firstLastName"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Input placeholder="Ej: González" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Apellido materno"
                name="secondLastName"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Input placeholder="Ej: Pérez" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="RUT"
                name="rut"
                rules={[
                  { required: true, message: "Requerido" },
                  {
                    validator: (_, val) =>
                      !val || validateRut(val) ? Promise.resolve() : Promise.reject("RUT inválido"),
                  },
                ]}
              >
                <Input placeholder="12.345.678-9" onBlur={onRutBlur("rut")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Sexo" name="sex">
                <Select placeholder="Seleccionar">
                  <Option value="M">Masculino</Option>
                  <Option value="F">Femenino</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Fecha de nacimiento" name="dob">
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" placeholder="DD/MM/AAAA" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Estado civil" name="civilStatus">
                <Select placeholder="Seleccionar">
                  <Option value="soltero">Soltero/a</Option>
                  <Option value="casado">Casado/a</Option>
                  <Option value="divorciado">Divorciado/a</Option>
                  <Option value="viudo">Viudo/a</Option>
                  <Option value="conviviente civil">Conviviente civil</Option>
                  <Option value="separado">Separado/a</Option>
                  <Option value="no informado">No informado</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Teléfono" name="phone">
                <Input placeholder="+56 9 1234 5678" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Email" name="email">
                <Input placeholder="tu@email.com" type="email" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Dirección */}
        <Card
          style={{ marginBottom: 16, borderRadius: 8 }}
          title={<Text strong>Dirección</Text>}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Dirección"
                name="address"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Input placeholder="Ej: Av. Diego Portales 123" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                label="Comuna"
                name="commune"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Select
                  showSearch
                  placeholder="Seleccionar"
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {COMMUNES.map((c) => <Option key={c} value={c}>{c}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Región" name="region">
                <Select
                  showSearch
                  placeholder="Seleccionar"
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {REGIONS.map((r) => <Option key={r} value={r}>{r}</Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Datos empresa (solo si entityType === 'company') */}
        {entityType === "company" && (
          <Card
            style={{ marginBottom: 16, borderRadius: 8 }}
            title={<Text strong>Datos de la empresa</Text>}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Razón social"
                  name="companyName"
                  rules={[{ required: true, message: "Requerido para empresa" }]}
                >
                  <Input placeholder="Ej: Importadora Sur S.A." />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="RUT empresa"
                  name="companyRut"
                  rules={[
                    { required: true, message: "Requerido" },
                    {
                      validator: (_, val) =>
                        !val || validateRut(val) ? Promise.resolve() : Promise.reject("RUT inválido"),
                    },
                  ]}
                >
                  <Input placeholder="76.123.456-7" onBlur={onRutBlur("companyRut")} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Giro comercial"
                  name="companyGiro"
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input placeholder="Ej: Compraventa de vehículos" />
                </Form.Item>
              </Col>
              <Divider orientation="left" style={{ margin: "8px 0 16px" }}>Representante legal</Divider>
              <Col xs={24} sm={12}>
                <Form.Item label="Nombre del representante legal" name="legalRepName">
                  <Input placeholder="Nombre completo" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label="RUT representante legal"
                  name="legalRepRut"
                  rules={[
                    {
                      validator: (_, val) =>
                        !val || validateRut(val) ? Promise.resolve() : Promise.reject("RUT inválido"),
                    },
                  ]}
                >
                  <Input placeholder="12.345.678-9" onBlur={onRutBlur("legalRepRut")} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        <Button
          type="primary"
          htmlType="submit"
          loading={saving}
          size="large"
          style={{ background: "#1d3557", borderColor: "#1d3557", minWidth: 180 }}
        >
          Guardar perfil
        </Button>
      </Form>
    </div>
  );
};

export default PortalProfile;
